import React, { useState } from 'react';
import { 
  Alert, 
  Button, 
  Box, 
  Typography, 
  CircularProgress,
  Snackbar
} from '@mui/material';
import { Email as EmailIcon, Refresh as RefreshIcon } from '@mui/icons-material';
import { sendVerificationEmail } from '../services/auth';
import { useAuth } from '../contexts/AuthContext';

const EmailVerificationReminder: React.FC = () => {
  const { user } = useAuth();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  const handleResendVerification = async () => {
    if (!user) return;
    
    try {
      setLoading(true);
      setError(null);
      await sendVerificationEmail(user);
      setSuccess(true);
    } catch (err) {
      setError('Failed to send verification email. Please try again later.');
      console.error('Error sending verification email:', err);
    } finally {
      setLoading(false);
    }
  };

  if (!user || user.emailVerified) {
    return null;
  }

  return (
    <Box sx={{ width: '100%', mb: 3 }}>
      <Alert 
        severity="warning"
        sx={{ 
          '& .MuiAlert-message': { 
            width: '100%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            flexWrap: 'wrap',
            gap: 2
          }
        }}
      >
        <Box>
          <Typography variant="body1" sx={{ mb: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
            <EmailIcon fontSize="small" />
            Please verify your email address
          </Typography>
          <Typography variant="body2" color="text.secondary">
            We sent a verification email to {user.email}. Please check your inbox and click the verification link.
          </Typography>
        </Box>
        <Button
          variant="outlined"
          color="warning"
          onClick={handleResendVerification}
          disabled={loading}
          startIcon={loading ? <CircularProgress size={20} /> : <RefreshIcon />}
          size="small"
        >
          {loading ? 'Sending...' : 'Resend Verification Email'}
        </Button>
      </Alert>

      <Snackbar
        open={success}
        autoHideDuration={6000}
        onClose={() => setSuccess(false)}
        message="Verification email sent! Please check your inbox."
      />

      {error && (
        <Snackbar
          open={!!error}
          autoHideDuration={6000}
          onClose={() => setError(null)}
          message={error}
        />
      )}
    </Box>
  );
};

export default EmailVerificationReminder; 